import React, { useEffect, useRef, useState } from 'react';

// ============================================================================
// TradeRail — resizable left-hand trade-group selector
// ============================================================================
// Shared by the Library and Rates pages so both expose the same trade picker.
// The width is persisted per-page (via `storageKey`); below COMPACT_RAIL_WIDTH
// the rail collapses to just trade codes + counts. Drag the divider to resize,
// double-click it to reset.
// ============================================================================

const DEFAULT_RAIL_WIDTH = 264;
const MIN_RAIL_WIDTH = 56;
const MAX_RAIL_WIDTH = 480;
const COMPACT_RAIL_WIDTH = 140;
const clampRail = (w) => Math.min(MAX_RAIL_WIDTH, Math.max(MIN_RAIL_WIDTH, Math.round(w)));

// `groups`: [{ key, tg: { code, label } | null, count, candidates? }]
// `selectedKey`: the active trade key, or null (e.g. while searching)
// `onSelect(key)`: called when a trade is clicked
// `containerRef`: ref of the flex row the rail lives in (for resize math)
export function TradeRail({ groups, selectedKey, onSelect, storageKey, containerRef }) {
  const [railWidth, setRailWidth] = useState(() => {
    const saved = Number(localStorage.getItem(storageKey));
    return saved >= MIN_RAIL_WIDTH && saved <= MAX_RAIL_WIDTH ? saved : DEFAULT_RAIL_WIDTH;
  });
  const localRef = useRef(null);
  const rowRef = containerRef || localRef;
  const compactRail = railWidth < COMPACT_RAIL_WIDTH;

  useEffect(() => { localStorage.setItem(storageKey, String(railWidth)); }, [storageKey, railWidth]);

  // Drag the divider to resize; listeners live on the document so the drag
  // keeps tracking even when the cursor leaves the thin handle.
  const startResize = (e) => {
    e.preventDefault();
    const onMove = (ev) => {
      const left = rowRef.current?.getBoundingClientRect().left ?? 0;
      setRailWidth(clampRail(ev.clientX - left));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  return (
    <>
      <div className="brio-analytics-surface" style={{ width: railWidth, flexShrink: 0, padding: '0.5rem', maxHeight: '74vh', overflowY: 'auto', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--brio-text-muted)', padding: '0.35rem 0.5rem' }}>
          <span>Trades</span>
          {!compactRail && <span style={{ textTransform: 'none', letterSpacing: 0 }}>{groups.length}</span>}
        </div>
        {groups.map((g) => {
          const active = g.key === selectedKey;
          return (
            <button
              key={g.key}
              type="button"
              className="qs-rail-btn"
              title={g.tg ? `${g.tg.code} · ${g.tg.label}` : 'Untagged'}
              onClick={() => onSelect(g.key)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', width: '100%', textAlign: 'left',
                padding: '0.4rem 0.5rem', border: 'none', borderRadius: 6, cursor: 'pointer',
                background: active ? 'var(--brio-gold, #9F902A)' : 'transparent',
                color: active ? '#fff' : 'var(--brio-text, #333)', marginBottom: 2,
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', minWidth: 0, flex: 1 }}>
                <code style={{
                  fontSize: '0.68rem', fontWeight: 700, padding: '0.05rem 0.32rem', borderRadius: 3, flexShrink: 0,
                  background: active ? 'rgba(255,255,255,0.22)' : 'var(--brio-surface-muted, #f0efea)',
                  color: active ? '#fff' : 'var(--brio-text-muted)',
                }}>{g.tg ? g.tg.code : '—'}</code>
                {!compactRail && (
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.84rem' }}>
                    {g.tg ? g.tg.label : 'Untagged'}
                  </span>
                )}
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}>
                {g.candidates > 0 && (
                  <span title={`${g.candidates} need reconciliation`} style={{ width: 7, height: 7, borderRadius: '50%', background: active ? '#fff' : 'var(--brio-gold, #9F902A)' }} />
                )}
                <span style={{ fontSize: '0.78rem', color: active ? 'rgba(255,255,255,0.85)' : 'var(--brio-text-muted)' }}>{g.count}</span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Drag-to-resize divider */}
      <div
        role="separator"
        aria-orientation="vertical"
        title="Drag to resize · double-click to reset"
        onMouseDown={startResize}
        onDoubleClick={() => setRailWidth(DEFAULT_RAIL_WIDTH)}
        className="boq-rail-resizer"
        style={{ flexShrink: 0, width: 12, alignSelf: 'stretch', cursor: 'col-resize', display: 'flex', justifyContent: 'center', alignItems: 'stretch' }}
      >
        <span style={{ width: 2, background: 'var(--brio-border, #ddd)', borderRadius: 2 }} />
      </div>
    </>
  );
}

export default TradeRail;
